"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { usePreferences } from "@/app/hooks/usePreferences";
import { useSession } from "@/app/hooks/useSession";
import { useSync } from "@/app/hooks/useSync";
import {
  clearPendingOperations,
  getSyncState,
  loadPendingOperations,
  loadState,
  savePendingOperations,
  saveState,
  saveSyncState,
} from "@/lib/storage";
import { applyOperations, createTimestamp } from "@/lib/sync/operations";
import type { AppState, Ingredient, IngredientList, Operation } from "@/lib/types";

export function useAppState() {
  // CRITICAL: Load localStorage IMMEDIATELY on mount (before any async operations)
  // This ensures zero-delay UI rendering regardless of auth/network state
  const [state, setState] = useState<AppState>(() => {
    console.log("🚀 Initial state load from localStorage");
    return loadState();
  });
  const [operationQueue, setOperationQueue] = useState<Operation[]>(() => {
    console.log("🚀 Loading pending operations from localStorage");
    return loadPendingOperations();
  });

  // Get auth state
  const { isAuthenticated, data: session } = useSession();
  const userId = session?.user?.id;

  // Get online status
  const { isOffline } = useOnlineStatus();

  // Get preferences management
  const { updatePreferences } = usePreferences();

  // Get sync capabilities
  // IMPORTANT: We expose syncState and isSyncing from useAppState because this hook
  // manages the automatic debounced sync operations (every 2 seconds). The performSync
  // function below calls syncOperations, which updates this sync state. If the page
  // used a separate useSync instance, it wouldn't reflect automatic sync operations.
  const {
    syncData,
    preferences: serverPreferences,
    syncOperations,
    syncState,
    isSyncing,
  } = useSync();

  // Tracking flags — persist hash in localStorage so all useAppState instances
  // share which server data was already processed (prevents stale cache overwrites on navigation)
  const SYNC_HASH_KEY = "nombox-sync-data-hash";
  const lastSyncedDataHash = useRef<string | null>(
    typeof window !== "undefined" ? localStorage.getItem(SYNC_HASH_KEY) : null
  );
  const isSyncingOperations = useRef(false);
  const wasOffline = useRef(isOffline);
  const pendingSyncRef = useRef<NodeJS.Timeout | null>(null);
  const previousUserId = useRef<string | undefined>(userId);
  const hasInitializedUserTracking = useRef(false);

  // USER LOGIN TRANSITION: Clear stale local state when user logs in
  // This handles the case where:
  // 1. User was unauthenticated and added items to a local list
  // 2. User logs in to an existing account
  // 3. The local operations in queue are for lists that don't exist on server
  // Without this fix, the old operations would prevent server data from merging
  useEffect(() => {
    // Skip the first run to avoid clearing data for already-logged-in users
    if (!hasInitializedUserTracking.current) {
      hasInitializedUserTracking.current = true;
      previousUserId.current = userId;
      return;
    }

    const wasUnauthenticated = previousUserId.current === undefined;
    const isNowAuthenticated = userId !== undefined;

    if (wasUnauthenticated && isNowAuthenticated) {
      console.log("🔑 User logged in - clearing stale local state to prepare for server sync");

      // Clear operation queue since those operations are for local-only lists
      setOperationQueue([]);
      clearPendingOperations();

      // Reset the server data hash so the next sync properly merges server data
      lastSyncedDataHash.current = null;
    }

    // Track the current userId for next comparison
    previousUserId.current = userId;
  }, [userId]);

  // Helper function to perform sync operations
  // This function calls syncOperations from the internal useSync hook, which triggers
  // a mutation that updates syncState (status becomes "syncing", then "success"/"error")
  // and sets isSyncing to true during the operation.
  const performSync = useCallback(
    async (operationsToSync: Operation[]) => {
      if (!syncOperations || operationsToSync.length === 0) return;

      // Prevent concurrent syncs
      if (isSyncingOperations.current) {
        console.log("⏸️ Sync already in progress, skipping duplicate sync");
        return;
      }

      isSyncingOperations.current = true;

      try {
        console.log(`💾 Syncing ${operationsToSync.length} operation(s)...`);
        const serverChanges = await syncOperations(operationsToSync);

        // Apply server changes to state, skipping conflicts with operations being synced
        if (serverChanges && serverChanges.length > 0) {
          console.log(`📥 Applying ${serverChanges.length} server change(s)`);
          setState((prevState) => {
            return applyOperations(prevState, serverChanges, operationsToSync);
          });
        }

        // Clear synced operations from queue
        setOperationQueue((currentQueue) => {
          return currentQueue.slice(operationsToSync.length);
        });

        // Also clear from localStorage
        clearPendingOperations();
      } catch (error: any) {
        console.error("❌ Sync failed:", error);

        // Keep operations in queue for retry
      } finally {
        isSyncingOperations.current = false;
      }
    },
    [syncOperations]
  );

  // Note: We intentionally do NOT clear localStorage on auth state changes
  // Reasons:
  // 1. Dev server restarts cause NextAuth session to reset temporarily
  // 2. Page refreshes can cause session to flicker
  // 3. Network issues can cause session to appear lost
  // 4. localStorage-first architecture means local data is always source of truth
  //
  // Instead, explicit logout is handled by:
  // - SignOut button in NavFooter calls signOut() which triggers NextAuth's signOut flow
  // - NextAuth clears its session cookie
  // - On next mount, useAppState starts fresh with loadState() (creates default list)
  //
  // For login:
  // - User signs in, NextAuth creates session
  // - Server sync fetches user's data and merges into localStorage
  // - hasLoadedServerData ensures we only merge once per session

  // SYNC FROM SERVER: When authenticated and server data arrives, merge it into localStorage
  useEffect(() => {
    // Skip if not authenticated or no server data
    if (!isAuthenticated || !syncData) return;

    // Calculate hash of current server data to detect if it's changed
    const currentServerHash = JSON.stringify(syncData);

    // Skip if we've already processed this exact server data
    if (lastSyncedDataHash.current === currentServerHash) {
      return;
    }

    console.log("📥 Server data available, merging with local state");

    // Check if we've already loaded server data for this user (persisted across sessions)
    const syncState = getSyncState();
    const isFirstLoginForUser = !syncState.hasLoadedServerData || syncState.userId !== userId;

    // If we have pending operations, it means we have local changes that need to be synced
    // HOWEVER: If this is first login for user, check if pending ops are for server lists
    // Operations from unauthenticated sessions reference local-only list IDs and should be cleared
    let hasPendingChanges = operationQueue.length > 0;

    // If first login and has pending ops, check if they're valid for server data
    if (isFirstLoginForUser && hasPendingChanges && syncData.lists.length > 0) {
      const serverListIds = new Set(syncData.lists.map((list: IngredientList) => list.id));
      // Collect all ingredient IDs from server lists for validation
      const serverIngredientIds = new Set(
        syncData.lists.flatMap((list: IngredientList) =>
          list.ingredients.map((ing: Ingredient) => ing.id)
        )
      );

      // Check if any pending operation references a list/ingredient that exists on the server
      const hasValidPendingOps = operationQueue.some((op) => {
        if (op.type === "ADD_LIST") {
          // ADD_LIST operations from unauthenticated sessions are for local-only lists
          // Their IDs are generated client-side and will never match server list IDs
          return false;
        } else if (op.type === "ADD_INGREDIENT") {
          return serverListIds.has(op.listId);
        } else if (op.type === "UPDATE_LIST" || op.type === "DELETE_LIST") {
          return serverListIds.has(op.id);
        } else if (op.type === "UPDATE_INGREDIENT" || op.type === "DELETE_INGREDIENT") {
          // Check if ingredient exists on server
          return serverIngredientIds.has(op.id);
        }
        return false;
      });

      if (!hasValidPendingOps) {
        console.log(
          "🧹 Clearing stale pending operations from unauthenticated session (no matching server lists)"
        );
        setOperationQueue([]);
        clearPendingOperations();
        hasPendingChanges = false;
      }
    }

    // Also check if local state differs from server state (more robust than just checking queue)
    const currentLocalState = loadState();
    const localStateHash = JSON.stringify(currentLocalState);
    const localStateDiffersFromServer = localStateHash !== currentServerHash;

    // Server data available - merge it
    if (syncData.lists.length === 0) {
      console.log("🆕 New authenticated user - server has no data, syncing local default list");

      // Server has no lists, sync our local default list to server
      if (currentLocalState.lists.length > 0) {
        // Queue operation to sync default list to server
        setOperationQueue([
          {
            type: "ADD_LIST",
            list: currentLocalState.lists[0],
            timestamp: createTimestamp(),
          },
        ]);
      }
    } else if (isFirstLoginForUser && !hasPendingChanges) {
      // First time loading server data for this user AND no pending changes
      // Use server as source of truth
      console.log(`📥 First sync: Merging ${syncData.lists.length} list(s) from server`);

      // Use activeListId from server preferences if available, otherwise fall back to sync data
      const serverActiveListId =
        (serverPreferences as any)?.activeListId &&
        syncData.lists.some((list: any) => list.id === (serverPreferences as any).activeListId)
          ? (serverPreferences as any).activeListId
          : syncData.activeListId;

      // Find the active list
      const activeList = serverActiveListId
        ? syncData.lists.find((list: any) => list.id === serverActiveListId)
        : null;

      // If active list doesn't exist or is disabled, select first non-disabled list
      let finalActiveListId = serverActiveListId;
      if (!activeList || activeList.isDisabled) {
        const firstAvailable = syncData.lists.find((list: any) => !list.isDisabled);
        finalActiveListId = firstAvailable ? firstAvailable.id : null;
        console.log(
          `🔄 Active list ${!activeList ? "deleted" : "disabled"}, switching to ${finalActiveListId || "none"}`
        );
        // Update preferences if activeListId changed
        if (finalActiveListId !== serverActiveListId) {
          updatePreferences({ activeListId: finalActiveListId });
        }
      }

      const stateFromServer = {
        ...syncData,
        activeListId: finalActiveListId,
      };

      // Update both React state and localStorage
      setState(stateFromServer);
      saveState(stateFromServer);

      // Clear any pending operations (server is source of truth on first login)
      setOperationQueue([]);

      // Mark that we've loaded server data for this user
      saveSyncState({ hasLoadedServerData: true, userId });
    } else {
      // Already loaded server data before OR have pending changes OR local state differs
      console.log(
        `🔄 ${hasPendingChanges ? "Pending changes detected" : localStateDiffersFromServer ? "Local state differs from server" : "Already synced"}`
      );

      // CRITICAL FIX: When local differs from server and we have NO pending changes,
      // TAKE SERVER AS SOURCE OF TRUTH. This ensures we get changes from other devices.
      if (!hasPendingChanges && localStateDiffersFromServer) {
        console.log(`📥 Taking server as source of truth (no pending local changes)`);

        // Use activeListId from server preferences if available
        const serverActiveListId =
          (serverPreferences as any)?.activeListId &&
          syncData.lists.some((list: any) => list.id === (serverPreferences as any).activeListId)
            ? (serverPreferences as any).activeListId
            : syncData.activeListId;

        // Find the active list
        const activeList = serverActiveListId
          ? syncData.lists.find((list: any) => list.id === serverActiveListId)
          : null;

        // If active list doesn't exist or is disabled, select first non-disabled list
        let finalActiveListId = serverActiveListId;
        if (!activeList || activeList.isDisabled) {
          const firstAvailable = syncData.lists.find((list: any) => !list.isDisabled);
          finalActiveListId = firstAvailable ? firstAvailable.id : null;
          console.log(
            `🔄 Active list ${!activeList ? "deleted" : "disabled"}, switching to ${finalActiveListId || "none"}`
          );
          // Update preferences if activeListId changed
          if (finalActiveListId !== serverActiveListId) {
            updatePreferences({ activeListId: finalActiveListId });
          }
        }

        const stateFromServer = {
          ...syncData,
          activeListId: finalActiveListId,
        };

        // Update both React state and localStorage
        setState(stateFromServer);
        saveState(stateFromServer);
      }
    }

    // Mark that we've processed this server data (persist so other instances skip stale cache)
    lastSyncedDataHash.current = currentServerHash;
    try {
      localStorage.setItem(SYNC_HASH_KEY, currentServerHash);
    } catch {}

    // Mark in persistent storage that we've loaded server data for this user
    saveSyncState({ hasLoadedServerData: true, userId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, syncData, serverPreferences, userId]);
  // Note: operationQueue.length is intentionally NOT in dependencies
  // We don't want this effect to re-run when operations are queued
  // This effect handles merging server data into local state

  // SAVE: Always save to localStorage immediately when state changes
  // localStorage is our source of truth - UI reads from it, server syncs with it
  useEffect(() => {
    saveState(state);
  }, [state]);

  // SAVE: Always save pending operations to localStorage when queue changes
  useEffect(() => {
    savePendingOperations(operationQueue);
  }, [operationQueue]);

  // SYNC: Batch sync operations with 500ms batching window (immediate response, smart batching)
  // When operations are queued, this effect waits 500ms to collect additional operations,
  // then calls performSync. This provides immediate feedback while preventing spam.
  useEffect(() => {
    if (
      !isAuthenticated ||
      operationQueue.length === 0 ||
      isSyncingOperations.current ||
      isOffline
    ) {
      return;
    }

    // Clear any existing timeout
    if (pendingSyncRef.current) {
      clearTimeout(pendingSyncRef.current);
    }

    // Schedule sync with short batching window (500ms)
    // This feels immediate to users while batching rapid operations
    pendingSyncRef.current = setTimeout(() => {
      const operationsToSync = [...operationQueue];
      performSync(operationsToSync);
      pendingSyncRef.current = null;
    }, 500); // 500ms batching - feels immediate, prevents spam

    return () => {
      if (pendingSyncRef.current) {
        clearTimeout(pendingSyncRef.current);
        pendingSyncRef.current = null;
      }
    };
  }, [operationQueue, isAuthenticated, isOffline, performSync]);

  // RECONNECT SYNC: Immediately sync when coming back online
  useEffect(() => {
    // Track if we just came back online
    if (wasOffline.current && !isOffline) {
      console.log("🌐 Network reconnected, triggering immediate sync");

      // Clear any pending batching timer to prevent duplicate sync
      if (pendingSyncRef.current) {
        clearTimeout(pendingSyncRef.current);
        pendingSyncRef.current = null;
      }

      // Sync immediately when reconnecting
      if (isAuthenticated && operationQueue.length > 0 && !isSyncingOperations.current) {
        const operationsToSync = [...operationQueue];
        performSync(operationsToSync);
      }
    }
    wasOffline.current = isOffline;
  }, [isOffline, isAuthenticated, operationQueue, performSync]);

  // BACKGROUND SYNC: Sync operations when app goes into background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "hidden" &&
        isAuthenticated &&
        operationQueue.length > 0 &&
        !isSyncingOperations.current
      ) {
        const operationsToSync = [...operationQueue];
        console.log(`📱 Background sync: ${operationsToSync.length} pending operation(s)`);
        // Fire-and-forget: don't await, let it run in background
        performSync(operationsToSync);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isAuthenticated && operationQueue.length > 0) {
        const data = JSON.stringify({
          operations: operationQueue,
        });

        // Use sendBeacon for guaranteed delivery (up to 64KB)
        if (data.length < 60000) {
          // Leave buffer for headers
          const success = navigator.sendBeacon("/api/sync", data);
          if (success) {
            console.log(`📡 Sent ${operationQueue.length} operation(s) via beacon`);
          } else {
            // Beacon failed, warn user
            e.preventDefault();
            e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
          }
        } else {
          // Too large for beacon, warn user
          e.preventDefault();
          e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isAuthenticated, operationQueue, performSync]);

  // Helper to queue an operation and update state
  const queueOperation = useCallback((operation: Operation) => {
    setOperationQueue((prev) => [...prev, operation]);
  }, []);

  // Replace or add operation - for debounced updates that should coalesce
  const replaceOrQueueOperation = useCallback((operation: Operation) => {
    setOperationQueue((prev) => {
      // For UPDATE_INGREDIENT operations, replace any existing operation for the same ingredient
      if (operation.type === "UPDATE_INGREDIENT") {
        const existingIndex = prev.findIndex(
          (op) => op.type === "UPDATE_INGREDIENT" && op.id === operation.id
        );
        if (existingIndex !== -1) {
          // Replace existing operation
          const newQueue = [...prev];
          newQueue[existingIndex] = operation;
          return newQueue;
        }
      }
      // Otherwise, just append
      return [...prev, operation];
    });
  }, []);

  const activeList = state.lists.find((list) => list.id === state.activeListId);

  return {
    state,
    setState,
    activeList,
    queueOperation, // Expose for use in other hooks
    replaceOrQueueOperation, // Expose for debounced operations
    // Expose sync state for UI components (SyncStatus) to show sync progress and badges.
    // This sync state reflects automatic sync operations managed by this hook.
    syncState,
    isSyncing,
  };
}
